import { useEffect, useState } from "react";
import { Loader2, PoundSterling, ShoppingBag, CalendarCheck, Star, MessageSquareWarning, TrendingUp, FileDown } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-card border border-border rounded-md p-5" data-testid={testid}>
    <Icon className="w-5 h-5 text-accent mb-4" strokeWidth={1.5} />
    <div className="text-3xl font-serif text-foreground">{value}</div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
  </div>
);

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/business/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/business/analytics/report.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "hub-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the report");
    } finally { setDownloading(false); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;
  const { totals, series, popular_services, popular_items, recent_feedback } = data;

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Analytics</h1>
          <p className="text-muted-foreground text-sm">How your hub is performing across the last 14 days.</p>
        </div>
        <Button onClick={downloadReport} disabled={downloading} data-testid="download-report-btn" className="bg-primary text-primary-foreground gap-1.5 shrink-0">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Monthly PDF
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={PoundSterling} label="Room service revenue" value={`£${totals.revenue.toFixed(2)}`} testid="stat-revenue" />
        <Stat icon={ShoppingBag} label="Orders" value={totals.orders} testid="stat-orders" />
        <Stat icon={CalendarCheck} label="Bookings" value={totals.bookings} testid="stat-bookings" />
        <Stat icon={Star} label={`Avg rating (${totals.feedback_count})`} value={totals.avg_rating || "—"} testid="stat-rating" />
        <Stat icon={MessageSquareWarning} label="Open complaints" value={totals.open_complaints} testid="stat-complaints" />
        <Stat icon={TrendingUp} label="Feedback received" value={totals.feedback_count} testid="stat-feedback" />
      </div>

      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-serif text-lg text-foreground mb-4">Revenue over time</h3>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={series} margin={{ left: -20, right: 8 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#C5A880" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#C5A880" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#999" />
            <YAxis tick={{ fontSize: 11 }} stroke="#999" />
            <Tooltip formatter={(v) => [`£${v}`, "Revenue"]} />
            <Area type="monotone" dataKey="revenue" stroke="#C5A880" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-serif text-lg text-foreground mb-4">Orders & bookings</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={series} margin={{ left: -20, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#999" />
            <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="orders" fill="#0A0D10" radius={[3, 3, 0, 0]} />
            <Bar dataKey="bookings" fill="#C5A880" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-md">
          <div className="p-5 border-b border-border"><h3 className="font-serif text-lg text-foreground">Popular services</h3></div>
          <div className="divide-y divide-border" data-testid="popular-services">
            {popular_services.length ? popular_services.map((s) => (
              <div key={s.name} className="p-4 flex items-center justify-between">
                <span className="text-sm text-foreground">{s.name}</span>
                <span className="text-sm text-muted-foreground">{s.count} booking{s.count === 1 ? "" : "s"}</span>
              </div>
            )) : <div className="p-8 text-center text-sm text-muted-foreground">No bookings yet.</div>}
          </div>
        </div>

        <div className="bg-card border border-border rounded-md">
          <div className="p-5 border-b border-border"><h3 className="font-serif text-lg text-foreground">Popular menu items</h3></div>
          <div className="divide-y divide-border" data-testid="popular-items">
            {popular_items.length ? popular_items.map((m) => (
              <div key={m.name} className="p-4 flex items-center justify-between">
                <span className="text-sm text-foreground">{m.name}</span>
                <span className="text-sm text-muted-foreground">{m.qty} sold · £{m.revenue.toFixed(2)}</span>
              </div>
            )) : <div className="p-8 text-center text-sm text-muted-foreground">No orders yet.</div>}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md">
        <div className="p-5 border-b border-border"><h3 className="font-serif text-lg text-foreground">Recent guest feedback</h3></div>
        <div className="divide-y divide-border" data-testid="recent-feedback">
          {recent_feedback.length ? recent_feedback.map((f) => (
            <div key={f.id} className="p-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{f.guest_name}{f.room ? ` · Room ${f.room}` : ""}</span>
                <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => (<Star key={n} className="w-3.5 h-3.5" style={{ color: n <= f.rating ? "#C5A880" : "#d4d4d8" }} fill={n <= f.rating ? "#C5A880" : "none"} />))}</span>
              </div>
              {f.comment && <p className="text-sm text-muted-foreground">{f.comment}</p>}
            </div>
          )) : <div className="p-8 text-center text-sm text-muted-foreground">No feedback yet.</div>}
        </div>
      </div>
    </div>
  );
}
